// src/lib/locationCatalog.ts
//
// Drop-in replacement for the `country-state-city` package, backed by the
// Locations module in the database (Settings → Locations).
//
// The package exposed synchronous getters over a bundled dataset. The whole
// catalogue here is small (tens of countries, hundreds of cities), so it is
// fetched once into a module-level cache and served synchronously from then
// on — which keeps every consuming screen's existing render logic intact.
//
// Screens call `useLocationCatalog()` once so they re-render when the fetch
// lands; everything else is the same call shape as before:
//   Country.getAllCountries() / State.getStatesOfCountry(iso) /
//   City.getCitiesOfState(iso, stateCode)

import React from "react";

import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

/* ============================ Public shapes ============================ */
/** Mirrors the package's ICountry for the fields screens actually read. */
export type ICountry = {
  name: string;
  isoCode: string;
  flag?: string;
  phonecode?: string;
  currency?: string;
};

/** A region. `isoCode` carries the region id, which is our stable key. */
export type IState = {
  name: string;
  isoCode: string;
  countryCode: string;
};

export type ICity = {
  name: string;
  countryCode: string;
  stateCode?: string;
  /** Stored as numeric(10,7) in the DB, so string like the old package. */
  latitude?: string | null;
  longitude?: string | null;
};

/* ============================== Raw rows =============================== */

type RawCountry = { iso2: string; name: string };
type RawRegion = { id: number; countryIso2: string; name: string; code?: string | null };
type RawCity = {
  id: number;
  countryIso2: string;
  regionId?: number | null;
  name: string;
  latitude?: string | null;
  longitude?: string | null;
};

type Catalog = {
  countries: ICountry[];
  states: IState[];
  cities: ICity[];
};

const EMPTY: Catalog = { countries: [], states: [], cities: [] };

let catalog: Catalog = EMPTY;
let status: "idle" | "loading" | "ready" | "error" = "idle";

const listeners = new Set<() => void>();
function notify() {
  listeners.forEach((fn) => fn());
}

function byName(a: { name: string }, b: { name: string }) {
  return a.name.localeCompare(b.name);
}

type CatalogPayload = {
  countries?: RawCountry[];
  regions?: RawRegion[];
  cities?: RawCity[];
};

/** Pages one of the list endpoints to completion (the API caps limit at 100). */
async function fetchAllPages<T>(path: string): Promise<T[]> {
  const PAGE_SIZE = 100;
  const out: T[] = [];

  for (let page = 1; ; page += 1) {
    const res = await request<{ data?: T[]; meta?: { total?: number } }>({
      method: "GET",
      url: `${PATHS.locations.base}${path}?page=${page}&limit=${PAGE_SIZE}`,
    });

    const rows = res?.data ?? [];
    out.push(...rows);

    const total = Number(res?.meta?.total ?? out.length);
    if (rows.length < PAGE_SIZE || out.length >= total) break;
  }

  return out;
}

/**
 * Falls back to the three list endpoints when /catalog is unavailable — e.g. an
 * API instance that predates it. Slower (a few round trips) but identical data.
 */
async function fetchCatalogPiecewise(): Promise<CatalogPayload> {
  const [countries, regions, cities] = await Promise.all([
    fetchAllPages<RawCountry>("/countries"),
    fetchAllPages<RawRegion>("/regions"),
    fetchAllPages<RawCity>("/cities"),
  ]);

  return { countries, regions, cities };
}

async function fetchCatalog(): Promise<Catalog> {
  let data: CatalogPayload;

  try {
    const res = await request<{ ok?: boolean; data?: CatalogPayload }>({
      method: "GET",
      url: `${PATHS.locations.base}/catalog`,
    });

    // The client unwraps some envelopes and not others — accept either shape.
    data = res?.data ?? (res as unknown as CatalogPayload);
  } catch {
    data = await fetchCatalogPiecewise();
  }

  const countries: ICountry[] = (data?.countries ?? [])
    .map((c: RawCountry) => ({ name: c.name, isoCode: c.iso2 }))
    .sort(byName);

  const states: IState[] = (data?.regions ?? [])
    .map((r: RawRegion) => ({
      name: r.name,
      isoCode: String(r.id),
      countryCode: r.countryIso2,
    }))
    .sort(byName);

  const cities: ICity[] = (data?.cities ?? [])
    .map((c: RawCity) => ({
      name: c.name,
      countryCode: c.countryIso2,
      stateCode: c.regionId != null ? String(c.regionId) : undefined,
      latitude: c.latitude ?? null,
      longitude: c.longitude ?? null,
    }))
    .sort(byName);

  return { countries, states, cities };
}

/** Loads the catalogue once. Safe to call from anywhere, any number of times. */
export function ensureLocationCatalog(): void {
  if (status !== "idle") return;
  status = "loading";

  fetchCatalog()
    .then((next) => {
      catalog = next;
      status = "ready";
    })
    .catch(() => {
      status = "error";
    })
    .finally(notify);
}

/**
 * Subscribes a screen to the catalogue: triggers the load on mount and
 * re-renders once it arrives.
 */
export function useLocationCatalog() {
  const [, forceRender] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    listeners.add(forceRender);
    ensureLocationCatalog();
    return () => {
      listeners.delete(forceRender);
    };
  }, []);

  return {
    isLoading: status === "loading" || status === "idle",
    isError: status === "error",
    isReady: status === "ready",
  };
}

/* ========================= Package-shaped API ========================== */

export const Country = {
  getAllCountries(): ICountry[] {
    ensureLocationCatalog();
    return catalog.countries;
  },

  getCountryByCode(isoCode?: string | null): ICountry | undefined {
    if (!isoCode) return undefined;
    const key = String(isoCode).trim().toUpperCase();
    return catalog.countries.find((c) => c.isoCode === key);
  },
};

export const State = {
  getStatesOfCountry(countryCode?: string | null): IState[] {
    ensureLocationCatalog();
    if (!countryCode) return [];
    const key = String(countryCode).trim().toUpperCase();
    return catalog.states.filter((s) => s.countryCode === key);
  },

  getStateByCodeAndCountry(
    stateCode?: string | null,
    countryCode?: string | null
  ): IState | undefined {
    if (!stateCode || !countryCode) return undefined;
    const country = String(countryCode).trim().toUpperCase();
    const code = String(stateCode).trim();
    return catalog.states.find(
      (s) => s.countryCode === country && s.isoCode === code
    );
  },
};

export const City = {
  /**
   * Cities of a region. Unlike the package, an empty `stateCode` returns every
   * city in the country rather than nothing — our cities may have no region,
   * and those would otherwise be unreachable.
   */
  getCitiesOfState(
    countryCode?: string | null,
    stateCode?: string | null
  ): ICity[] {
    ensureLocationCatalog();
    if (!countryCode) return [];

    const country = String(countryCode).trim().toUpperCase();
    const inCountry = catalog.cities.filter((c) => c.countryCode === country);

    if (!stateCode) return inCountry;

    const code = String(stateCode).trim();
    return inCountry.filter((c) => c.stateCode === code);
  },

  getCitiesOfCountry(countryCode?: string | null): ICity[] {
    return City.getCitiesOfState(countryCode, null);
  },

  getAllCities(): ICity[] {
    ensureLocationCatalog();
    return catalog.cities;
  },
};
